(function () {
  'use strict';

  // Additive first-party client contract (same paths behind the configured base):
  // GET base -> snapshot with redemption, bonusActions, petProfile and actionToken.
  // snapshot.redemption.flexible selects the mutually exclusive redeem shape:
  // v2 {amountKr,idempotencyKey,actionToken}; legacy v1 {rewardKey,idempotencyKey,actionToken}.
  // POST base/action {actionKey,idempotencyKey,actionToken}.
  // POST base/profile {name,type,breed,birthDate,birthDateApproximate,slot?,idempotencyKey,actionToken}.
  // v3 additions (all optional, older backends omit them): snapshot.pets, maxPets, petBirthdays,
  // customerAwards and reviewOffer; POST base/action {actionKey:'pet_birthday',slot} and
  // {actionKey:'media_review',productId}.
  // Opt-in membership (2026-09-22): snapshot.membership {member, joinedAt}. Non-members get the
  // full snapshot but every mutation answers 403 NOT_A_MEMBER; POST base/join {idempotencyKey,
  // actionToken} enrols them and returns {membership, duplicate, snapshot}.
  // Consent-based membership (2026-09-23): snapshot.membership {member, status NONE|ACTIVE|PAUSED,
  // joinedAt, pausedAt, pauseReason, needsPhone}. member is true only when status is ACTIVE; a
  // PAUSED member (unsubscribed from email or SMS) is treated like a non-member on every mutation.
  // POST base/join {idempotencyKey, actionToken, phone?} subscribes to email + SMS marketing (the
  // click is the consent) and answers 400 PHONE_REQUIRED / 400 INVALID_PHONE when the phone is
  // missing or malformed.

  function interpolate(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, function (_match, key) {
      return values[key] == null ? '' : String(values[key]);
    });
  }

  function initializeCalculator(calculator) {
    if (calculator.dataset.initialized === 'true') return;
    var configNode = document.getElementById(calculator.dataset.configId || '');
    var input = calculator.querySelector('[data-calculator-spend]');
    var output = calculator.querySelector('[data-calculator-output]');
    if (!configNode || !input || !output) return;

    var config;
    try { config = JSON.parse(configNode.textContent || '{}'); } catch (_error) { return; }
    var calculatorConfig = config.calculator || {};
    var locale = config.locale || 'sv-SE';
    var bonusPoints = Math.max(0, Math.floor(Number(calculatorConfig.bonusPoints) || 0));
    var earnPointsPerKr = Math.floor(Number(calculatorConfig.earnPointsPerKr) || 0);
    var pointsPerRewardKr = Math.floor(Number(calculatorConfig.pointsPerRewardKr) || 100);
    // The rate comes from the section config. Without it the server-rendered example stays as is.
    if (earnPointsPerKr < 1 || pointsPerRewardKr < 1) return;
    var pointsOutput = calculator.querySelector('[data-calculator-points]');
    var valueOutput = calculator.querySelector('[data-calculator-value]');
    var totalOutput = calculator.querySelector('[data-calculator-total]');
    var breakdownOutput = calculator.querySelector('[data-calculator-breakdown]');
    var quickButtons = Array.prototype.slice.call(calculator.querySelectorAll('[data-calculator-quick]'));
    if (!pointsOutput || !valueOutput || !totalOutput || !breakdownOutput) return;

    function number(value) {
      return new Intl.NumberFormat(locale, {maximumFractionDigits:0}).format(Number(value) || 0);
    }
    function sync() {
      var monthlySpend = Math.max(0, Math.min(100000, Math.floor(Number(input.value) || 0)));
      var annualPoints = monthlySpend * 12 * earnPointsPerKr;
      var annualValueKr = Math.floor(annualPoints / pointsPerRewardKr);
      var firstYearValueKr = Math.floor((annualPoints + bonusPoints) / pointsPerRewardKr);
      pointsOutput.textContent = interpolate(calculatorConfig.pointsPattern, {points:number(annualPoints)});
      valueOutput.textContent = interpolate(calculatorConfig.moneyPattern, {amount:number(annualValueKr)});
      totalOutput.textContent = interpolate(calculatorConfig.moneyPattern, {amount:number(firstYearValueKr)});
      breakdownOutput.textContent = interpolate(calculatorConfig.breakdownPattern, {points:number(annualPoints), bonus:number(bonusPoints)});
      quickButtons.forEach(function (button) {
        button.setAttribute('aria-pressed', String(Number(button.dataset.calculatorQuick) === monthlySpend));
      });
    }

    input.addEventListener('input', sync);
    quickButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        input.value = String(Number(button.dataset.calculatorQuick) || 0);
        sync();
        input.focus();
      });
    });
    calculator.dataset.initialized = 'true';
    sync();
  }

  document.querySelectorAll('[data-dj-loyalty-calculator]').forEach(initializeCalculator);

  function node(tag, className, content) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    if (content != null) element.appendChild(document.createTextNode(String(content)));
    return element;
  }
  function formatNumber(locale, value) {
    return new Intl.NumberFormat(locale || 'sv-SE').format(Number(value) || 0);
  }
  function formatMoney(locale, currency, cents) {
    return new Intl.NumberFormat(locale || 'sv-SE', {
      style:'currency', currency:currency || 'SEK', maximumFractionDigits:0
    }).format((Number(cents) || 0) / 100);
  }
  function percent(value) {
    return Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
  }
  function sameOriginPath(value) {
    var path = String(value || '');
    return /^\/(?!\/)/.test(path) ? path : '';
  }

  // Milestones (v3, 2026-09-22): snapshot.milestones {items, nextKey, reachedKey, messageKey, trackPercent}
  // for members; GET base/milestones {milestones} for guests (no progress fields).
  // POST base/redeem {milestoneKey,idempotencyKey,actionToken} claims one; the reward's applyPath
  // is a same-origin storefront path that puts the code (and a product reward) in the cart.
  var MILESTONE_ICONS = {
    gift:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><path d="M3.5 9h17v4h-17zM5 13h14v7.5H5zM12 9v11.5M12 9c-3.2 0-5.2-1.3-5.2-3.1a2 2 0 0 1 3.9-.7C11.2 6.6 12 9 12 9Zm0 0c3.2 0 5.2-1.3 5.2-3.1a2 2 0 0 0-3.9-.7C12.8 6.6 12 9 12 9Z"/></svg>',
    treat:'<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="5.3" cy="7.3" r="2.6"/><circle cx="8.3" cy="4.3" r="2.6"/><circle cx="18.7" cy="16.7" r="2.6"/><circle cx="15.7" cy="19.7" r="2.6"/><path d="M6.8 6.8 17.2 17.2" stroke="currentColor" stroke-width="4.2" stroke-linecap="round"/></svg>',
    toy:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="8.5"/><path d="M6 6c3.2 1.6 5 4.4 5 7.7 0 2.2-.7 4.2-2 5.9M18 6c-3.2 1.6-5 4.4-5 7.7 0 2.2.7 4.2 2 5.9" stroke-linecap="round"/></svg>',
    star:'<svg viewBox="0 0 24 24" fill="currentColor"><path d="m12 2.8 2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.7l-5.9 3.1 1.2-6.5L2.5 9.7l6.6-.9z"/></svg>',
    bed:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><path d="M3 18v-7.5A2.5 2.5 0 0 1 5.5 8h13A2.5 2.5 0 0 1 21 10.5V18M3 15h18M3 18v2M21 18v2"/><rect x="6" y="11" width="5" height="3" rx="1"/></svg>',
    brush:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h10v6a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3zM9 13v8M6.5 4v3M9 4v3M11.5 4v3M16 6h4M16 9h4M16 12h4"/></svg>',
    crown:'<svg viewBox="0 0 24 24" fill="currentColor"><path d="m3 7 4.5 4L12 4l4.5 7L21 7l-1.6 11H4.6zM4.6 19.5h14.8v1.7H4.6z"/></svg>',
    heart:'<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 20.6 4.3 13a4.7 4.7 0 0 1 6.6-6.6l1.1 1.1 1.1-1.1a4.7 4.7 0 0 1 6.6 6.6z"/></svg>',
    paw:'<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="6" cy="9.5" r="2.3"/><circle cx="12" cy="6.2" r="2.3"/><circle cx="18" cy="9.5" r="2.3"/><path d="M12 11.5c-3.9 0-6.9 3.8-5.5 6.9 1.1 2.4 3.5 1.2 5.5 1.2s4.4 1.2 5.5-1.2c1.4-3.1-1.6-6.9-5.5-6.9Z"/></svg>'
  };
  function normalizeMilestones(list, guest) {
    return (Array.isArray(list) ? list : []).map(function (item) {
      if (!item || typeof item !== 'object' || !item.key || !item.title) return null;
      var imageUrl = String(item.imageUrl || '');
      var points = Math.max(0, Math.floor(Number(item.points) || 0));
      var reached = !guest && item.reached === true;
      return {
        key:String(item.key),
        title:String(item.title),
        description:String(item.description || ''),
        points:points,
        amountKr:Math.max(0, Number(item.amountKr) || 0),
        kind:String(item.kind || 'any'),
        icon:String(item.icon || 'gift'),
        imageUrl:/^https:\/\//.test(imageUrl) ? imageUrl : '',
        productTitle:String(item.productTitle || ''),
        productHandle:String(item.productHandle || ''),
        reached:reached,
        pointsLeft:guest || item.pointsLeft == null ? (reached ? 0 : points) : Math.max(0, Math.floor(Number(item.pointsLeft) || 0)),
        krToShop:guest || item.krToShop == null ? 0 : Math.max(0, Math.ceil(Number(item.krToShop) || 0)),
        progressPercent:guest ? 0 : (reached ? 100 : percent(item.progressPercent))
      };
    }).filter(Boolean).sort(function (left, right) { return left.points - right.points; });
  }
  function milestoneByKey(items, key) {
    for (var index = 0; index < items.length; index += 1) {
      if (items[index].key === key) return items[index];
    }
    return null;
  }
  function milestoneMessage(view, next, reached) {
    var labels = view.labels;
    var patterns = {
      first_steps:labels.milestoneMessageFirstSteps,
      on_your_way:labels.milestoneMessageOnYourWay,
      almost_there:labels.milestoneMessageAlmostThere,
      reached:labels.milestoneMessageReached,
      all_reached:labels.milestoneMessageAllReached
    };
    var pattern = Object.prototype.hasOwnProperty.call(patterns, view.messageKey) ? patterns[view.messageKey] : '';
    if (!pattern) return '';
    var focus = view.messageKey === 'reached' ? (reached || next) : next;
    return interpolate(pattern, {
      points:next ? formatNumber(view.locale, next.pointsLeft) : '',
      kr:next ? formatNumber(view.locale, next.krToShop) : '',
      title:focus ? focus.title : '',
      next:next ? next.title : ''
    }).trim();
  }
  function milestoneResultBox(view, item) {
    var labels = view.labels;
    var result = view.result;
    if (!result || result.key !== item.key) return null;
    if (result.status !== 'success') {
      var code = String(result.code || '');
      var text = labels.milestoneError;
      if (result.status === 'uncertain') text = labels.milestoneUncertain;
      else if (code === 'INSUFFICIENT_POINTS') text = labels.milestoneInsufficient;
      else if (code === 'PROGRAM_NOT_ACTIVE') text = labels.programPausedText;
      var errorBox = node('div', 'dj-loyalty__message dj-loyalty__message--error');
      errorBox.setAttribute('role', 'alert'); errorBox.tabIndex = -1; errorBox.dataset.djFocusKey = 'milestone-result';
      errorBox.appendChild(node('p', '', text));
      return errorBox;
    }
    var reward = result.reward || {};
    var box = node('div', 'dj-loyalty__message dj-loyalty__message--success');
    box.setAttribute('role', 'status'); box.tabIndex = -1; box.dataset.djFocusKey = 'milestone-result';
    box.appendChild(node('p', '', interpolate(reward.kind === 'product' ? labels.milestoneSuccessProductPattern : labels.milestoneSuccessCodePattern, {title:reward.title, code:reward.code})));
    var actions = node('div', 'dj-loyalty-ui__actions');
    var cart = node('a', 'dj-loyalty-ui__button dj-loyalty-ui__button--primary', labels.milestoneCart);
    cart.href = view.cartUrl || '/cart';
    actions.appendChild(cart);
    if (reward.code && typeof view.copyText === 'function') {
      var copy = node('button', 'dj-loyalty-ui__button dj-loyalty-ui__button--secondary', labels.copy); copy.type = 'button';
      var copyStatus = node('span', 'dj-loyalty-ui__copy-status'); copyStatus.setAttribute('role', 'status');
      copy.addEventListener('click', function () {
        view.copyText(String(reward.code)).then(function () { copy.textContent = labels.copied; copyStatus.textContent = labels.copied; window.setTimeout(function () { copy.textContent = labels.copy; }, 2200); })
          .catch(function () { copyStatus.textContent = labels.copyFailed; });
      });
      actions.appendChild(copy); box.append(actions, copyStatus);
    } else {
      box.appendChild(actions);
    }
    return box;
  }
  function milestoneCard(view, item, next) {
    var labels = view.labels;
    var busy = !view.guest && view.busyKey === item.key;
    var status = view.guest ? 'guest' : (item.reached ? 'reached' : (next && next.key === item.key ? 'next' : 'locked'));
    var card = node('article', 'dj-loyalty-ui__milestone dj-loyalty-ui__milestone--' + status);
    if (item.imageUrl) {
      var image = node('img', 'dj-loyalty-ui__milestone-image');
      image.src = item.imageUrl; image.alt = ''; image.width = 72; image.height = 72; image.loading = 'lazy';
      card.appendChild(image);
    } else {
      var icon = node('span', 'dj-loyalty-ui__milestone-icon');
      icon.setAttribute('aria-hidden', 'true');
      // Static markup only; the icon key selects one of our own SVGs.
      icon.innerHTML = Object.prototype.hasOwnProperty.call(MILESTONE_ICONS, item.icon) ? MILESTONE_ICONS[item.icon] : MILESTONE_ICONS.gift;
      card.appendChild(icon);
    }
    if (status === 'reached') card.appendChild(node('span', 'dj-loyalty-ui__status-pill dj-loyalty-ui__status-pill--complete', labels.milestoneReachedPill));
    else if (status === 'next') card.appendChild(node('span', 'dj-loyalty-ui__status-pill', labels.milestoneNextPill));
    card.appendChild(node('h4', '', item.title));
    card.appendChild(node('p', 'dj-loyalty-ui__milestone-value', interpolate(labels.milestoneValuePattern, {
      points:formatNumber(view.locale, item.points), amount:formatMoney(view.locale, view.currency, item.amountKr * 100)
    })));
    if (item.description) card.appendChild(node('p', '', item.description));
    if (status === 'next') {
      card.appendChild(node('p', 'dj-loyalty-ui__milestone-left', interpolate(labels.milestoneNextPattern, {
        points:formatNumber(view.locale, item.pointsLeft), kr:formatNumber(view.locale, item.krToShop)
      })));
      var progress = node('div', 'dj-loyalty-ui__milestone-progress');
      progress.setAttribute('role', 'progressbar'); progress.setAttribute('aria-valuemin', '0'); progress.setAttribute('aria-valuemax', '100');
      progress.setAttribute('aria-valuenow', String(item.progressPercent));
      progress.setAttribute('aria-label', interpolate(labels.milestoneProgressAriaPattern, {percent:item.progressPercent, title:item.title}));
      var bar = node('span'); bar.style.width = String(item.progressPercent) + '%'; progress.appendChild(bar);
      card.appendChild(progress);
    } else if (status === 'locked') {
      card.appendChild(node('p', 'dj-loyalty-ui__milestone-left', interpolate(labels.milestoneLockedPattern, {points:formatNumber(view.locale, item.pointsLeft)})));
    }
    if (busy) {
      var progressBox = node('div', 'dj-loyalty__message dj-loyalty__message--info');
      progressBox.setAttribute('role', 'status'); progressBox.tabIndex = -1; progressBox.dataset.djFocusKey = 'milestone-progress';
      progressBox.appendChild(node('p', '', labels.milestoneClaiming));
      card.appendChild(progressBox);
    }
    var resultBox = milestoneResultBox(view, item);
    if (resultBox) card.appendChild(resultBox);
    if (status === 'reached' || busy) {
      var actions = node('div', 'dj-loyalty-ui__actions');
      var claim = node('button', 'dj-loyalty-ui__button dj-loyalty-ui__button--primary', busy ? labels.milestoneClaiming : labels.milestoneClaim);
      claim.type = 'button'; claim.disabled = busy || !view.canClaim;
      claim.setAttribute('aria-label', interpolate(labels.milestoneClaimAriaPattern, {title:item.title}));
      claim.dataset.djFocusKey = 'milestone-claim-' + item.key.replace(/[^a-zA-Z0-9_-]/g, '-');
      claim.addEventListener('click', function () { if (typeof view.onClaim === 'function') view.onClaim(item.key); });
      actions.appendChild(claim); card.appendChild(actions);
    }
    return card;
  }
  function milestoneTrack(view) {
    var labels = view.labels;
    var items = view.items;
    if (!items.length) return null;
    var section = node('section', 'dj-loyalty-ui__section dj-loyalty-ui__milestones');
    if (view.heading) section.appendChild(node('h3', '', view.heading));
    if (view.lead) section.appendChild(node('p', 'dj-loyalty-ui__help', view.lead));
    var next = view.guest ? null : milestoneByKey(items, view.nextKey);
    var reached = view.guest ? null : milestoneByKey(items, view.reachedKey);
    var text = view.guest ? '' : milestoneMessage(view, next, reached);
    if (text) {
      var line = node('p', 'dj-loyalty-ui__milestone-message', text);
      line.setAttribute('role', 'status');
      section.appendChild(line);
    }
    // Markers sit at even intervals regardless of their point values (owner decision 2026-09-23);
    // the fill runs to the last reached marker plus the progress towards the next one.
    var count = items.length;
    function markerPosition(index) { return index < 0 ? 0 : (index + 0.5) / count * 100; }
    var reachedCount = items.filter(function (item) { return item.reached; }).length;
    var nextProgress = next ? percent(next.progressPercent) : 0;
    var fillPercent = reachedCount >= count
      ? 100
      : markerPosition(reachedCount - 1) + (markerPosition(reachedCount) - markerPosition(reachedCount - 1)) * nextProgress / 100;
    fillPercent = Math.max(0, Math.min(100, Math.round(fillPercent * 10) / 10));
    var wrap = node('div', 'dj-loyalty-ui__track-wrap');
    var track = node('div', 'dj-loyalty-ui__track');
    track.setAttribute('role', 'progressbar'); track.setAttribute('aria-valuemin', '0'); track.setAttribute('aria-valuemax', '100');
    track.setAttribute('aria-valuenow', String(Math.round(fillPercent)));
    track.setAttribute('aria-label', labels.milestoneTrackAria);
    var fill = node('span', 'dj-loyalty-ui__track-fill'); fill.style.width = String(fillPercent) + '%';
    track.appendChild(fill);
    items.forEach(function (item, index) {
      var marker = node('span', 'dj-loyalty-ui__track-marker' + (item.reached ? ' dj-loyalty-ui__track-marker--reached' : '') + (next && next.key === item.key ? ' dj-loyalty-ui__track-marker--next' : ''));
      marker.setAttribute('aria-hidden', 'true');
      marker.style.left = String(Math.round(markerPosition(index) * 100) / 100) + '%';
      marker.appendChild(node('span', '', formatMoney(view.locale, view.currency, item.amountKr * 100)));
      track.appendChild(marker);
    });
    wrap.appendChild(track); section.appendChild(wrap);
    var cards = node('div', 'dj-loyalty-ui__milestone-cards');
    items.forEach(function (item) { cards.appendChild(milestoneCard(view, item, next)); });
    section.appendChild(cards);
    if (view.guest && view.joinUrl) {
      var actions = node('div', 'dj-loyalty-ui__actions');
      var join = node('a', 'dj-loyalty-ui__button dj-loyalty-ui__button--primary', labels.guestPrimary);
      join.href = view.joinUrl;
      actions.appendChild(join); section.appendChild(actions);
    }
    return section;
  }
  function initializeGuestMilestones(container) {
    var root = container.querySelector('[data-milestones-root]');
    var configNode = document.getElementById(container.dataset.configId || '');
    if (!root || !configNode) return;
    var config;
    try { config = JSON.parse(configNode.textContent || '{}'); } catch (_error) { return; }
    var endpoint = String(container.dataset.endpoint || '/apps/djurslottet-poang').replace(/\/$/, '');
    fetch(endpoint + '/milestones', {credentials:'same-origin', headers:{Accept:'application/json'}})
      .then(function (response) { return response.ok ? response.json() : Promise.reject(new Error('MILESTONES_UNAVAILABLE')); })
      .then(function (payload) {
        var items = normalizeMilestones(payload && payload.milestones, true);
        var track = milestoneTrack({
          labels:config.labels || {}, locale:config.locale, currency:config.currency, heading:'', lead:'',
          items:items, trackPercent:0, nextKey:'', reachedKey:'', messageKey:'none',
          guest:true, joinUrl:String(container.dataset.joinUrl || '')
        });
        if (!track) return;
        root.replaceChildren(track);
        container.hidden = false;
      })
      .catch(function () {});
  }

  // Guests: the same track from the public list, so they see what points can unlock.
  document.querySelectorAll('[data-dj-loyalty-milestones]').forEach(initializeGuestMilestones);

  document.querySelectorAll('[data-dj-loyalty-dashboard]').forEach(function (container) {
    if (container.dataset.authenticated !== 'true') return;
    var root = container.querySelector('[data-dashboard-root]');
    var configNode = document.getElementById(container.dataset.configId || '');
    var staticBonus = container.closest('.dj-loyalty') && container.closest('.dj-loyalty').querySelector('[data-dj-loyalty-static-bonus]');
    if (!root || !configNode) return;

    var config;
    try { config = JSON.parse(configNode.textContent || '{}'); } catch (_error) { return; }
    var labels = config.labels || {};
    var bonusDefinitions = Array.isArray(config.bonusDefinitions) ? config.bonusDefinitions : [];
    var endpoint = String(container.dataset.endpoint || '/apps/djurslottet-poang').replace(/\/$/, '');
    var instanceId = String(container.id || 'DjLoyalty').replace(/[^a-zA-Z0-9_-]/g, '-');
    var state = {
      snapshot:null,
      loading:true,
      loadError:false,
      redemptionAmountKr:'',
      pendingRedemptionAmountKr:null,
      redeemAttemptKey:null,
      redeeming:false,
      redeemError:null,
      redeemSuccess:false,
      lastRedeemedRewardId:null,
      actionBusyKey:null,
      actionAttemptKeys:{},
      actionErrorKey:null,
      actionSuccessKey:null,
      petForms:{},
      petFormInitialized:false,
      petEditing:{},
      profileSaving:false,
      profileSlot:1,
      profileAttemptKeys:{},
      profileError:null,
      profileSuccess:null,
      awardBusyKey:null,
      awardAttemptKeys:{},
      awardResult:null,
      milestoneClaimKey:null,
      milestoneAttemptKeys:{},
      milestoneResult:null,
      joining:false,
      joinAttemptKey:null,
      joinError:false,
      joinSuccess:false,
      joinPhone:'',
      joinPhoneError:null,
      joinPhoneRequested:false,
      focusTarget:null
    };

    function replace(template, values) {
      return String(template || '').replace(/\{(\w+)\}/g, function (_match, key) {
        return values[key] == null ? '' : String(values[key]);
      });
    }
    function number(value) {
      return new Intl.NumberFormat(config.locale || 'sv-SE').format(Number(value) || 0);
    }
    function money(cents) {
      return new Intl.NumberFormat(config.locale || 'sv-SE', {
        style:'currency', currency:config.currency || 'SEK', maximumFractionDigits:0
      }).format((Number(cents) || 0) / 100);
    }
    function date(value, long) {
      // long = the written-out month ("22 september 2026"), used for the membership date.
      var parsed = value ? new Date(value) : null;
      return parsed && !Number.isNaN(parsed.getTime())
        ? new Intl.DateTimeFormat(config.locale || 'sv-SE', {year:'numeric', month:long ? 'long' : 'short', day:'numeric'}).format(parsed)
        : '';
    }
    function dayDate(value) {
      // Calendar days (YYYY-MM-DD) are shown as that day in every time zone.
      var parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
      if (!parts) return date(value);
      return date(new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]), 12, 0, 0, 0));
    }
    function pointsValue(pointValue) {
      var perKr = state.snapshot ? state.snapshot.redemption.pointsPerRewardKr : 100;
      return money(Math.floor((Number(pointValue) || 0) * 100 / perKr));
    }
    function normalizePet(source, fallbackSlot) {
      if (!source || typeof source !== 'object') return null;
      var slot = Math.floor(Number(source.slot) || fallbackSlot || 0);
      if (slot < 1) return null;
      return {
        slot:slot,
        name:String(source.name || ''),
        type:String(source.type || source.animalType || '').toUpperCase(),
        breed:String(source.breed || ''),
        birthDate:String(source.birthDate || '').slice(0, 10),
        birthDateApproximate:Boolean(source.birthDateApproximate || source.approximateBirthDate)
      };
    }
    function isCompletePet(pet) {
      return Boolean(pet && pet.name && pet.type && pet.breed && pet.birthDate);
    }
    function localIsoDate(dayOffset) {
      var local = new Date();
      local.setHours(12, 0, 0, 0);
      local.setDate(local.getDate() + (Number(dayOffset) || 0));
      var month = String(local.getMonth() + 1).padStart(2, '0');
      var day = String(local.getDate()).padStart(2, '0');
      return String(local.getFullYear()) + '-' + month + '-' + day;
    }
    function points(value) {
      var numericValue = Number(value) || 0;
      return number(numericValue) + ' ' + (Math.abs(numericValue) === 1 ? labels.point : labels.points);
    }
    function createIdempotencyKey() {
      return window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : String(Date.now()) + '-' + Math.random().toString(16).slice(2);
    }
    function isCompletedAction(action) {
      var status = String(action && action.status || '').toUpperCase();
      return Boolean(action && action.completed) || ['COMPLETED', 'CLAIMED', 'EARNED', 'DONE'].indexOf(status) !== -1;
    }
    function normalize(payload) {
      var source = payload && (payload.snapshot || payload.data || payload);
      if (!source || typeof source !== 'object') throw new Error('Invalid loyalty response');
      var availablePoints = Math.max(0, Number(source.availablePoints) || 0);
      var redemptionSource = source.redemption && typeof source.redemption === 'object' ? source.redemption : {};
      var flexible = redemptionSource.flexible === true;
      var rewards = Array.isArray(source.rewards) ? source.rewards : [];
      var availableLegacyAmountsKr = rewards.filter(function (reward) {
        return reward && reward.unlocked === true && typeof reward.key === 'string'
          && Number.isInteger(Number(reward.amountCents)) && Number(reward.amountCents) > 0
          && Number(reward.amountCents) % 100 === 0;
      }).map(function (reward) { return Number(reward.amountCents) / 100; });
      var pointsPerRewardKr = Math.max(1, Math.floor(Number(redemptionSource.pointsPerRewardKr || redemptionSource.pointsPerKr) || 100));
      var serverMaxAmountKr = Number(redemptionSource.maxAmountKr);
      var maxAmountKr = flexible
        ? (Number.isFinite(serverMaxAmountKr)
          ? Math.max(0, Math.floor(serverMaxAmountKr))
          : Math.max(0, Math.floor(availablePoints / pointsPerRewardKr)))
        : availableLegacyAmountsKr.reduce(function (maximum, amount) { return Math.max(maximum, amount); }, 0);
      var quickSource = flexible
        ? (Array.isArray(redemptionSource.quickAmountsKr) ? redemptionSource.quickAmountsKr : [5, 10, 25, 50])
        : availableLegacyAmountsKr;
      var quickAmountsKr = quickSource.map(Number).filter(function (amount, index, values) {
        return Number.isInteger(amount) && amount > 0 && values.indexOf(amount) === index;
      }).sort(function (left, right) { return left - right; });
      var serverActions = Array.isArray(source.bonusActions) ? source.bonusActions : [];
      var normalizedActions = serverActions.map(function (action) {
        return {
          actionKey:String(action && (action.actionKey || action.key) || ''),
          points:Math.max(0, Number(action && action.points) || 0),
          completed:isCompletedAction(action),
          status:String(action && action.status || '')
        };
      }).filter(function (action) { return action.actionKey; });
      bonusDefinitions.forEach(function (definition) {
        if (!normalizedActions.some(function (action) { return action.actionKey === definition.actionKey; })) {
          normalizedActions.push({actionKey:String(definition.actionKey || ''), points:0, completed:false, status:'AVAILABLE'});
        }
      });
      var profileSource = source.petProfile && typeof source.petProfile === 'object' ? source.petProfile : null;
      // Older backends omit pets, maxPets, petBirthdays, customerAwards and reviewOffer.
      var multiPet = Array.isArray(source.pets);
      var pets = (multiPet ? source.pets : []).map(function (pet) { return normalizePet(pet, 0); })
        .filter(isCompletePet).sort(function (left, right) { return left.slot - right.slot; });
      var primaryPet = normalizePet(profileSource, 1);
      if (primaryPet) primaryPet.slot = 1;
      if (!pets.length && isCompletePet(primaryPet)) pets = [primaryPet];
      var petBirthdays = (Array.isArray(source.petBirthdays) ? source.petBirthdays : []).map(function (entry) {
        if (!entry || typeof entry !== 'object') return null;
        var slot = Math.floor(Number(entry.slot) || 0);
        if (slot < 1) return null;
        return {
          slot:slot,
          name:String(entry.name || ''),
          year:Math.floor(Number(entry.year) || 0),
          birthday:String(entry.birthday || '').slice(0, 10),
          windowOpensAt:String(entry.windowOpensAt || '').slice(0, 10),
          windowClosesAt:String(entry.windowClosesAt || '').slice(0, 10),
          inWindow:entry.inWindow === true,
          profileOldEnough:entry.profileOldEnough === true,
          claimed:entry.claimed === true,
          claimable:entry.claimable === true,
          points:Math.max(0, Number(entry.points) || 0)
        };
      }).filter(Boolean);
      var awardsSource = source.customerAwards && typeof source.customerAwards === 'object' ? source.customerAwards : {};
      var reviewSource = source.reviewOffer && typeof source.reviewOffer === 'object' ? source.reviewOffer : {};
      var reviewProducts = (Array.isArray(reviewSource.products) ? reviewSource.products : []).map(function (product) {
        if (!product || typeof product !== 'object' || !product.productId || !product.title) return null;
        var imageUrl = String(product.imageUrl || '');
        return {
          productId:String(product.productId),
          title:String(product.title),
          handle:String(product.handle || ''),
          imageUrl:/^https:\/\//.test(imageUrl) ? imageUrl : '',
          orderName:String(product.orderName || ''),
          awarded:product.awarded === true
        };
      }).filter(Boolean);
      var milestoneSource = source.milestones && typeof source.milestones === 'object' ? source.milestones : {};
      // Older backends omit membership; they treated every logged-in customer as a member.
      // Status-less backends (2026-09-22) only send member; status arrived with consent-based
      // membership (2026-09-23) and wins when present. member is true only when status is ACTIVE.
      var membershipSource = source.membership && typeof source.membership === 'object' ? source.membership : null;
      var membershipStatus = 'ACTIVE';
      if (membershipSource) {
        membershipStatus = String(membershipSource.status || (membershipSource.member === true ? 'ACTIVE' : 'NONE')).toUpperCase();
        if (membershipStatus !== 'ACTIVE' && membershipStatus !== 'PAUSED') membershipStatus = 'NONE';
        if (membershipStatus === 'ACTIVE' && membershipSource.member === false) membershipStatus = 'NONE';
      }
      return {
        programStatus:String(source.programStatus || 'DRAFT'),
        membership:{
          member:membershipStatus === 'ACTIVE',
          status:membershipStatus,
          joinedAt:membershipSource && membershipSource.joinedAt ? String(membershipSource.joinedAt) : '',
          pausedAt:membershipSource && membershipSource.pausedAt ? String(membershipSource.pausedAt) : '',
          pauseReason:membershipSource && membershipSource.pauseReason ? String(membershipSource.pauseReason) : '',
          needsPhone:Boolean(membershipSource && membershipSource.needsPhone === true)
        },
        availablePoints:availablePoints,
        rawBalancePoints:Number(source.rawBalancePoints) || 0,
        debtPoints:Math.max(0, Number(source.debtPoints) || 0),
        pendingPoints:Math.max(0, Number(source.pendingPoints) || 0),
        redemption:{
          earnPointsPerKr:Math.max(1, Number(redemptionSource.earnPointsPerKr) || 1),
          pointsPerRewardKr:pointsPerRewardKr,
          maxAmountKr:maxAmountKr,
          quickAmountsKr:quickAmountsKr,
          flexible:flexible
        },
        rewards:rewards,
        activeRewards:Array.isArray(source.activeRewards)
          ? source.activeRewards.filter(function (reward) { return String(reward && reward.status || '').toUpperCase() === 'ACTIVE'; })
          : [],
        bonusActions:normalizedActions,
        milestones:{
          items:normalizeMilestones(milestoneSource.items, false),
          nextKey:String(milestoneSource.nextKey || ''),
          reachedKey:String(milestoneSource.reachedKey || ''),
          messageKey:String(milestoneSource.messageKey || 'none'),
          trackPercent:percent(milestoneSource.trackPercent)
        },
        pets:pets,
        multiPet:multiPet,
        maxPets:multiPet ? Math.max(1, Math.floor(Number(source.maxPets) || 1)) : 1,
        petBirthdays:petBirthdays,
        customerAwards:{
          additionalPetPoints:Math.max(0, Number(awardsSource.additionalPetPoints) || 0),
          petBirthdayPoints:Math.max(0, Number(awardsSource.petBirthdayPoints) || 0),
          mediaReviewPoints:Math.max(0, Number(awardsSource.mediaReviewPoints) || 0)
        },
        reviewOffer:{
          enabled:reviewSource.enabled === true,
          points:Math.max(0, Number(reviewSource.points) || Number(awardsSource.mediaReviewPoints) || 0),
          products:reviewProducts
        },
        petProfile:profileSource ? {
          name:String(profileSource.name || ''),
          type:String(profileSource.type || profileSource.animalType || '').toUpperCase(),
          breed:String(profileSource.breed || ''),
          birthDate:String(profileSource.birthDate || '').slice(0, 10),
          birthDateApproximate:Boolean(profileSource.birthDateApproximate || profileSource.approximateBirthDate)
        } : null,
        expiring:source.expiring || null,
        history:Array.isArray(source.history) ? source.history : [],
        actionToken:String(source.actionToken || '')
      };
    }
    function emptyPetForm() {
      return {name:'', type:'', breed:'', birthDate:'', birthDateApproximate:false};
    }
    function petFormValues(pet) {
      return {
        name:String(pet.name || ''), type:String(pet.type || ''), breed:String(pet.breed || ''),
        birthDate:String(pet.birthDate || '').slice(0, 10), birthDateApproximate:Boolean(pet.birthDateApproximate)
      };
    }
    function petForm(petNumber) {
      if (!state.petForms[petNumber]) state.petForms[petNumber] = emptyPetForm();
      return state.petForms[petNumber];
    }
    function savedPet(snapshot, petNumber) {
      var pets = snapshot && Array.isArray(snapshot.pets) ? snapshot.pets : [];
      for (var index = 0; index < pets.length; index += 1) {
        if (pets[index].slot === petNumber) return pets[index];
      }
      return null;
    }
    function resetPetForm(petNumber) {
      var pet = savedPet(state.snapshot, petNumber) || (petNumber === 1 && state.snapshot ? state.snapshot.petProfile : null);
      state.petForms[petNumber] = pet ? petFormValues(pet) : emptyPetForm();
    }
    function initializePetForm(snapshot) {
      if (state.petFormInitialized) return;
      snapshot.pets.forEach(function (pet) { state.petForms[pet.slot] = petFormValues(pet); });
      if (!state.petForms[1] && snapshot.petProfile) state.petForms[1] = petFormValues(snapshot.petProfile);
      state.petFormInitialized = true;
    }
    function profileMatches(profile, submitted) {
      if (!profile || !submitted) return false;
      return String(profile.name || '').trim() === String(submitted.name || '').trim()
        && String(profile.type || '').trim().toUpperCase() === String(submitted.type || '').trim().toUpperCase()
        && String(profile.breed || '').trim() === String(submitted.breed || '').trim()
        && String(profile.birthDate || '').slice(0, 10) === String(submitted.birthDate || '').slice(0, 10)
        && Boolean(profile.birthDateApproximate) === Boolean(submitted.birthDateApproximate);
    }
    function profileActionCompleted(snapshot) {
      return Boolean(snapshot && snapshot.bonusActions && snapshot.bonusActions.some(function (action) {
        return action.actionKey === 'pet_profile_completed' && action.completed;
      }));
    }
    function profileSaveWasApplied(snapshot, submitted, alreadyCompleted, petNumber) {
      if (petNumber > 1) return profileMatches(savedPet(snapshot, petNumber), submitted);
      return profileMatches(savedPet(snapshot, 1) || (snapshot && snapshot.petProfile), submitted)
        && (alreadyCompleted || profileActionCompleted(snapshot));
    }
    function profileSuccessKind(snapshot, alreadyCompleted, payload, petNumber) {
      if (payload && payload.bonusAwarded === true) return 'bonus';
      if (petNumber > 1) return 'updated';
      return !alreadyCompleted && profileActionCompleted(snapshot) ? 'bonus' : 'updated';
    }
    function fallbackCopy(text) {
      var previousFocus = document.activeElement;
      var textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.pointerEvents = 'none';
      document.body.appendChild(textarea);
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);
      var copied = false;
      try { copied = document.execCommand('copy'); } catch (_error) { copied = false; }
      textarea.remove();
      if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
      return copied;
    }
    function copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text).catch(function () {
          if (fallbackCopy(text)) return;
          throw new Error('COPY_FAILED');
        });
      }
      return fallbackCopy(text) ? Promise.resolve() : Promise.reject(new Error('COPY_FAILED'));
    }
    function request(url, options) {
      return fetch(url, Object.assign({credentials:'same-origin', headers:{Accept:'application/json'}}, options || {}))
        .then(function (response) {
          return response.json().catch(function () { return {}; }).then(function (payload) {
            if (!response.ok) {
              var apiError = payload && payload.error;
              var error = new Error(apiError && apiError.message || payload.message || String(apiError || 'Request failed'));
              error.code = String(apiError && apiError.code || payload.code || 'LOYALTY_REQUEST_FAILED');
              error.status = response.status;
              throw error;
            }
            return payload;
          });
        });
    }
    function snapshotFromMutation(payload) {
      if (payload && (payload.snapshot || payload.data || payload.availablePoints != null)) return Promise.resolve(normalize(payload));
      return request(endpoint).then(normalize);
    }
    function message(kind, heading, body, retry, focusKey) {
      var box = node('div', 'dj-loyalty__message dj-loyalty__message--' + kind);
      box.setAttribute('role', kind === 'error' ? 'alert' : 'status');
      if (focusKey) { box.tabIndex = -1; box.dataset.djFocusKey = focusKey; }
      if (heading) box.appendChild(node('h3', '', heading));
      if (body) box.appendChild(node('p', '', body));
      if (retry) {
        var button = node('button', 'dj-loyalty-ui__button dj-loyalty-ui__button--secondary', labels.retry);
        button.type = 'button';
        button.addEventListener('click', function () { load(true); });
        box.appendChild(button);
      }
      return box;
    }
    function stat(label, value, help) {
      var card = node('article', 'dj-loyalty-ui__card');
      card.appendChild(node('span', 'dj-loyalty-ui__stat-label', label));
      card.appendChild(node('strong', 'dj-loyalty-ui__stat-value', value));
      if (help) card.appendChild(node('p', 'dj-loyalty-ui__help', help));
      return card;
    }
    function restoreFocus(focusKey) {
      if (!focusKey) return;
      var targets = root.querySelectorAll('[data-dj-focus-key]');
      for (var index = 0; index < targets.length; index += 1) {
        if (targets[index].dataset.djFocusKey === focusKey && typeof targets[index].focus === 'function') {
          targets[index].focus(); return;
        }
      }
    }
    function isMutationBusy() {
      return state.redeeming || Boolean(state.actionBusyKey) || state.profileSaving || Boolean(state.awardBusyKey) || Boolean(state.milestoneClaimKey) || state.joining;
    }
    // Opt-in panel for a logged-in customer who is not an active member (owner decisions 2026-09-22
    // and 2026-09-23): free, never automatic, and the join click is the consent to email + SMS
    // marketing. A PAUSED member (unsubscribed) sees the paused variant of the same panel; the
    // points stay but nothing can be earned or used until they join again. The milestone track
    // follows in its guest form so the customer sees what the points can unlock before joining.
    function joinPanel() {
      var snapshot = state.snapshot;
      var membership = snapshot.membership;
      var paused = membership.status === 'PAUSED';
      var panel = node('section', 'dj-loyalty-ui__section dj-loyalty-ui__join' + (paused ? ' dj-loyalty-ui__join--paused' : ''));
      var headingId = instanceId + '-join-heading';
      panel.setAttribute('aria-labelledby', headingId);
      panel.appendChild(node('p', 'dj-loyalty__section-kicker', labels.membershipKicker));
      var heading = node('h3', '', paused ? labels.membershipPausedHeading : labels.membershipHeading); heading.id = headingId;
      panel.appendChild(heading);
      if (paused) {
        var pausedText = snapshot.availablePoints > 0
          ? replace(labels.membershipPausedTextPattern, {points:number(snapshot.availablePoints)})
          : labels.membershipPausedTextNoPoints;
        if (pausedText) panel.appendChild(node('p', 'dj-loyalty-ui__join-paused', pausedText));
      } else {
        var benefits = node('ul', 'dj-loyalty-ui__join-benefits');
        [labels.membershipBenefitOne, labels.membershipBenefitTwo, labels.membershipBenefitThree].forEach(function (text) {
          if (!text) return;
          var item = node('li');
          var mark = node('span', '', '✓'); mark.setAttribute('aria-hidden', 'true');
          item.append(mark, node('span', '', text)); benefits.appendChild(item);
        });
        panel.appendChild(benefits);
        if (snapshot.availablePoints > 0) {
          panel.appendChild(node('p', 'dj-loyalty-ui__join-waiting', replace(labels.membershipPointsWaitingPattern, {points:number(snapshot.availablePoints)})));
        }
      }
      if (state.joining) panel.appendChild(message('info', '', labels.membershipJoining, false, 'join-progress'));
      if (state.joinError) panel.appendChild(message('error', '', labels.membershipError, false, 'join-error'));
      var form = node('form', 'dj-loyalty-ui__join-form');
      form.noValidate = true;
      form.addEventListener('submit', function (event) { event.preventDefault(); join(); });
      // The phone is required for the SMS subscription when Shopify has none on file.
      if (membership.needsPhone || state.joinPhoneRequested || state.joinPhoneError) form.appendChild(joinPhoneField());
      // Consent line directly above the button: the click subscribes to email + SMS offers.
      if (labels.membershipConsent) form.appendChild(node('p', 'dj-loyalty-ui__join-consent', labels.membershipConsent));
      var actions = node('div', 'dj-loyalty-ui__actions');
      var buttonLabel = paused ? (labels.membershipRejoin || labels.membershipJoin) : labels.membershipJoin;
      var button = node('button', 'dj-loyalty-ui__button dj-loyalty-ui__button--primary', state.joining ? labels.membershipJoining : buttonLabel);
      button.type = 'submit'; button.disabled = state.joining; button.dataset.djFocusKey = 'join-button';
      actions.appendChild(button); form.appendChild(actions); panel.appendChild(form);
      var terms = node('p', 'dj-loyalty-ui__join-terms', labels.membershipTerms);
      if (config.rulesUrl && labels.membershipTermsLink) {
        var link = node('a', '', labels.membershipTermsLink); link.href = config.rulesUrl;
        terms.append(' ', link);
      }
      panel.appendChild(terms);
      return panel;
    }
    function joinPhoneField() {
      var wrapper = node('div', 'dj-loyalty-ui__field dj-loyalty-ui__join-phone');
      var id = instanceId + '-join-phone';
      var label = node('label', '', labels.membershipPhoneLabel); label.htmlFor = id;
      var input = node('input', 'dj-loyalty-ui__input');
      input.id = id; input.name = 'phone'; input.type = 'tel'; input.inputMode = 'tel'; input.autocomplete = 'tel'; input.required = true;
      input.placeholder = labels.membershipPhonePlaceholder || ''; input.value = state.joinPhone; input.disabled = state.joining;
      input.dataset.djFocusKey = 'join-phone';
      wrapper.append(label, input);
      if (state.joinPhoneError) {
        var fieldError = node('p', 'dj-loyalty-ui__help dj-loyalty-ui__field-error', state.joinPhoneError === 'invalid' ? labels.membershipPhoneInvalid : labels.membershipPhoneRequired);
        fieldError.id = id + '-error'; fieldError.setAttribute('role', 'alert');
        input.setAttribute('aria-invalid', 'true'); input.setAttribute('aria-describedby', fieldError.id);
        wrapper.appendChild(fieldError);
      }
      input.addEventListener('input', function () { state.joinPhone = input.value; });
      return wrapper;
    }
    function guestMilestones(items) {
      // Preview form: no progress, no reached state, so the track renders without claim buttons.
      return items.map(function (item) {
        return Object.assign({}, item, {reached:false, pointsLeft:item.points, krToShop:0, progressPercent:0});
      });
    }
    function parseAmount(value) {
      var numeric = Number(value);
      if (!Number.isInteger(numeric) || numeric < 1 || !state.snapshot || numeric > state.snapshot.redemption.maxAmountKr) return null;
      if (!state.snapshot.redemption.flexible && !legacyRewardForAmount(numeric, true)) return null;
      return numeric;
    }
    function legacyRewardForAmount(amountKr, requireUnlocked) {
      if (!state.snapshot || state.snapshot.redemption.flexible) return null;
      return state.snapshot.rewards.find(function (reward) {
        return reward && (!requireUnlocked || reward.unlocked === true) && typeof reward.key === 'string'
          && Number(reward.amountCents) === amountKr * 100;
      }) || null;
    }
    function redemptionPanel() {
      var snapshot = state.snapshot;
      var section = node('section', 'dj-loyalty-ui__section');
      section.appendChild(node('h3', '', labels.redemptionHeading));
      if (snapshot.redemption.flexible) section.appendChild(node('p', 'dj-loyalty-ui__help', labels.redemptionText));
      var layout = node('div', 'dj-loyalty-ui__redeem-layout');
      var panel = node('form', 'dj-loyalty-ui__redeem-panel');
      panel.noValidate = true;
      var field = node('div', 'dj-loyalty-ui__field');
      var inputId = instanceId + '-amount';
      var errorId = inputId + '-error';
      var label = node('label', '', labels.redemptionAmountLabel);
      label.htmlFor = inputId;
      var input = node('input', 'dj-loyalty-ui__input');
      input.id = inputId; input.name = 'amountKr'; input.type = 'number'; input.inputMode = 'numeric'; input.min = '1'; input.step = '1'; input.max = String(snapshot.redemption.maxAmountKr);
      input.value = state.redemptionAmountKr;
      input.disabled = snapshot.redemption.maxAmountKr < 1 || snapshot.programStatus !== 'ACTIVE' || isMutationBusy() || state.pendingRedemptionAmountKr != null;
      input.dataset.djFocusKey = 'redemption-amount';
      var maxHelp = node('p', 'dj-loyalty-ui__help', replace(labels.redemptionMaxPattern, {amount:money(snapshot.redemption.maxAmountKr * 100)}));
      var inputError = node('p', 'dj-loyalty-ui__help', '');
      inputError.id = errorId;
      field.append(label, input, maxHelp, inputError);
      if (snapshot.redemption.flexible) panel.appendChild(field);

      var quick = node('fieldset', 'dj-loyalty-ui__quick');
      var legend = node('legend', 'visually-hidden', labels.redemptionQuickAria);
      quick.appendChild(legend);
      var quickButtons = [];
      snapshot.redemption.quickAmountsKr.forEach(function (amountKr) {
        var button = node('button', 'dj-loyalty-ui__quick-button', money(amountKr * 100));
        button.type = 'button';
        button.disabled = amountKr > snapshot.redemption.maxAmountKr || snapshot.programStatus !== 'ACTIVE' || isMutationBusy() || state.pendingRedemptionAmountKr != null;
        button.setAttribute('aria-label', replace(labels.redemptionQuickPattern, {amount:money(amountKr * 100)}));
        button.setAttribute('aria-pressed', String(Number(state.redemptionAmountKr) === amountKr));
        if (!snapshot.redemption.flexible && quickButtons.length === 0) button.dataset.djFocusKey = 'redemption-amount';
        button.addEventListener('click', function () {
          state.redemptionAmountKr = String(amountKr);
          input.value = state.redemptionAmountKr;
          syncInput();
          if (snapshot.redemption.flexible) input.focus(); else button.focus();
        });
        quickButtons.push({button:button, amount:amountKr});
        quick.appendChild(button);
      });
      panel.appendChild(quick);

      var calculation = node('div', 'dj-loyalty-ui__calculation');
      var calculationText = node('span', '', labels.redemptionUnavailable);
      var calculationValue = node('strong', '', points(0));
      calculation.append(calculationText, calculationValue);
      panel.appendChild(calculation);
      var continueButton = node('button', 'dj-loyalty-ui__button dj-loyalty-ui__button--primary', labels.redeem);
      continueButton.type = 'submit';
      continueButton.dataset.djFocusKey = 'redemption-continue';
      var actions = node('div', 'dj-loyalty-ui__actions');
      actions.appendChild(continueButton); panel.appendChild(actions);
      if (state.redeeming) {
        continueButton.textContent = labels.redeeming; continueButton.disabled = true;
        panel.appendChild(message('info', '', labels.redeeming, false, 'redemption-confirm'));
      }
      if (state.redeemError && state.pendingRedemptionAmountKr != null) panel.appendChild(redemptionRetry());
      if (state.redeemSuccess) {
        var createdReward = null;
        snapshot.activeRewards.forEach(function (reward) { if (String(reward.id) === String(state.lastRedeemedRewardId)) createdReward = reward; });
        var result = node('div', 'dj-loyalty-ui__redeem-result'); result.tabIndex = -1; result.dataset.djFocusKey = 'redeem-success';
        result.setAttribute('role', 'status');
        result.appendChild(message('success', labels.redeemSuccess, createdReward ? labels.redeemSuccessCodeHelp : '', false));
        if (createdReward) result.appendChild(voucherCard(createdReward));
        panel.appendChild(result);
      }

      function syncInput(showError) {
        var amountKr = parseAmount(input.value);
        state.redemptionAmountKr = input.value;
        input.setAttribute('aria-invalid', String(Boolean(showError && amountKr == null)));
        if (showError && amountKr == null) { input.setAttribute('aria-describedby', errorId); inputError.textContent = labels.redemptionInvalid; }
        else { input.removeAttribute('aria-describedby'); inputError.textContent = ''; }
        quickButtons.forEach(function (entry) { entry.button.setAttribute('aria-pressed', String(amountKr === entry.amount)); });
        continueButton.disabled = amountKr == null || snapshot.programStatus !== 'ACTIVE' || isMutationBusy() || state.pendingRedemptionAmountKr != null;
        if (amountKr == null) {
          calculationText.textContent = snapshot.redemption.maxAmountKr < 1
            ? labels.redemptionUnavailable
            : (snapshot.redemption.flexible ? labels.redemptionText : labels.redemptionHeading);
          calculationValue.textContent = points(0);
        } else {
          var cost = amountKr * snapshot.redemption.pointsPerRewardKr;
          calculationText.textContent = replace(labels.redemptionCostPattern, {amount:money(amountKr * 100), points:number(cost)});
          calculationValue.textContent = points(cost);
        }
      }
      input.addEventListener('input', function () { state.redeemError = null; syncInput(false); });
      panel.addEventListener('submit', function (event) {
        event.preventDefault();
        var amountKr = parseAmount(input.value);
        if (amountKr == null) { syncInput(true); input.focus(); return; }
        state.redemptionAmountKr = String(amountKr);
        state.pendingRedemptionAmountKr = amountKr;
        state.redeemAttemptKey = createIdempotencyKey();
        state.redeemError = null; state.redeemSuccess = false; state.lastRedeemedRewardId = null;
        // One click creates the code. No second confirmation step (owner decision 2026-09-18).
        redeem();
      });
      syncInput(false);
      layout.appendChild(panel);

      var explanation = node('div', 'dj-loyalty-ui__redeem-panel');
      explanation.appendChild(node('strong', '', labels.redemptionEquation));
      explanation.appendChild(node('p', 'dj-loyalty-ui__help', labels.confirmTerms));
      layout.appendChild(explanation);
      section.appendChild(layout);
      return section;
    }
    // Shown only when a redemption attempt failed: retry keeps the same idempotency key.
    function redemptionRetry() {
      var box = node('div', 'dj-loyalty-ui__confirm');
      box.setAttribute('role', 'region'); box.setAttribute('aria-label', labels.redeemErrorHeading); box.tabIndex = -1; box.dataset.djFocusKey = 'redemption-confirm';
      box.appendChild(message('error', labels.redeemErrorHeading, state.redeemError === 'uncertain' ? labels.redeemUncertainText : labels.redeemErrorText, false, 'redeem-error'));
      var actions = node('div', 'dj-loyalty-ui__actions');
      var cancel = node('button', 'dj-loyalty-ui__button dj-loyalty-ui__button--secondary', labels.cancel);
      cancel.type = 'button'; cancel.disabled = state.redeeming || state.redeemError === 'uncertain';
      cancel.addEventListener('click', function () { state.pendingRedemptionAmountKr = null; state.redeemAttemptKey = null; state.redeemError = null; state.focusTarget = 'redemption-amount'; render(); });
      var retry = node('button', 'dj-loyalty-ui__button dj-loyalty-ui__button--primary', state.redeeming ? labels.redeeming : labels.retry);
      retry.type = 'button'; retry.disabled = state.redeeming; retry.addEventListener('click', redeem);
      actions.append(cancel, retry); box.appendChild(actions); return box;
    }
    function bonusAction(actionKey) {
      return state.snapshot.bonusActions.find(function (action) { return action.actionKey === actionKey; }) || {actionKey:actionKey, completed:false, points:0};
    }
    function bonusSection() {
      var section = node('section', 'dj-loyalty-ui__section');
      section.appendChild(node('h3', '', labels.bonusHeading));
      section.appendChild(node('p', 'dj-loyalty-ui__help', labels.bonusLead));
      if (state.actionBusyKey) section.appendChild(message('info', '', labels.bonusConfirming, false, 'bonus-progress'));
      if (state.actionSuccessKey) section.appendChild(message('success', '', labels.bonusSuccess, false, 'bonus-success'));
      if (state.actionErrorKey) section.appendChild(message('error', '', labels.bonusError, false, 'bonus-error'));
      var grid = node('div', 'dj-loyalty-ui__bonus-grid');
      bonusDefinitions.filter(function (definition) { return definition.kind !== 'pet_profile'; }).forEach(function (definition) {
        var action = bonusAction(definition.actionKey);
        var card = node('article', 'dj-loyalty-ui__bonus-card');
        card.appendChild(node('span', 'dj-loyalty-ui__status-pill' + (action.completed ? ' dj-loyalty-ui__status-pill--complete' : ''), action.completed ? labels.bonusCompleted : definition.pointsLabel));
        card.appendChild(node('h4', '', definition.heading));
        card.appendChild(node('p', '', definition.text));
        var actions = node('div', 'dj-loyalty-ui__actions');
        // One button: opens the profile in a new tab and registers the bonus (owner decision 2026-09-18).
        var open = node('a', 'dj-loyalty-ui__button dj-loyalty-ui__button--primary', state.actionBusyKey === definition.actionKey ? labels.bonusConfirming : (action.completed ? definition.openLabel : definition.confirmLabel));
        open.href = definition.url; open.target = '_blank'; open.rel = 'noopener noreferrer';
        open.dataset.djFocusKey = 'bonus-action-' + String(definition.actionKey || '').replace(/[^a-zA-Z0-9_-]/g, '-');
        if (!action.completed) {
          if (isMutationBusy() || state.snapshot.programStatus !== 'ACTIVE') open.setAttribute('aria-disabled', 'true');
          open.addEventListener('click', function () {
            if (isMutationBusy() || state.snapshot.programStatus !== 'ACTIVE') return;
            claimBonus(definition.actionKey);
          });
        }
        actions.appendChild(open); card.appendChild(actions); grid.appendChild(card);
      });
      section.appendChild(grid);
      section.appendChild(node('p', 'dj-loyalty__disclosure', labels.bonusDisclosure));
      section.appendChild(petSection());
      return section;
    }
    function field(labelText, name, type, petNumber) {
      var values = petForm(petNumber);
      var wrapper = node('div', 'dj-loyalty-ui__field');
      var id = instanceId + '-profile-' + (petNumber > 1 ? String(petNumber) + '-' : '') + name;
      var label = node('label', '', labelText); label.htmlFor = id;
      var input = node('input', 'dj-loyalty-ui__input');
      input.id = id; input.name = name; input.type = type || 'text'; input.required = true;
      input.value = values[name] || '';
      if (state.profileError === 'required' && state.profileSlot === petNumber && !String(values[name] || '').trim()) {
        var fieldError = node('span', 'dj-loyalty-ui__help', labels.fieldRequired);
        fieldError.id = id + '-error'; input.setAttribute('aria-invalid', 'true'); input.setAttribute('aria-describedby', fieldError.id);
        wrapper.append(label, input, fieldError);
      } else {
        wrapper.append(label, input);
      }
      input.addEventListener('input', function () { values[name] = input.value; state.profileError = null; state.profileAttemptKeys[petNumber] = null; });
      return {wrapper:wrapper, input:input};
    }
    function petTypeLabel(type) {
      if (type === 'DOG') return labels.profileTypeDog;
      if (type === 'HORSE') return labels.profileTypeHorse;
      if (type === 'CAT') return labels.profileTypeCat;
      return labels.profileTypeOther;
    }
    function additionalPetPill() {
      var bonus = state.snapshot.customerAwards.additionalPetPoints;
      return bonus > 0 ? node('span', 'dj-loyalty-ui__status-pill', replace(labels.petAddBonusPattern, {points:number(bonus), amount:pointsValue(bonus)})) : null;
    }
    function profileMessages(target, petNumber) {
      if (state.profileSlot !== petNumber) return;
      if (state.profileSaving) target.appendChild(message('info', '', labels.profileSaving, false, 'profile-progress'));
      if (state.profileSuccess) {
        var bonus = state.snapshot.customerAwards.additionalPetPoints;
        var successText = labels.profileUpdated;
        if (state.profileSuccess === 'bonus') {
          successText = petNumber > 1
            ? (bonus > 0 ? replace(labels.petAddedPattern, {points:number(bonus), amount:pointsValue(bonus)}) : labels.profileUpdated)
            : labels.profileSaved;
        }
        target.appendChild(message('success', '', successText, false, 'profile-success'));
      }
      if (state.profileError) {
        var errorText = labels.profileError;
        if (state.profileError === 'required') errorText = labels.profileRequired;
        else if (state.profileError === 'birthDate') errorText = labels.profileBirthDateInvalid || labels.profileError;
        else if (state.profileError === 'paused') errorText = labels.programPausedText || labels.profileError;
        target.appendChild(message('error', '', errorText, false, 'profile-error'));
      }
    }
    function openPetForm(petNumber) {
      if (isMutationBusy()) return;
      resetPetForm(petNumber);
      state.petEditing[petNumber] = true; state.profileError = null; state.profileSuccess = null;
      state.focusTarget = 'pet-name-' + petNumber; render();
    }
    function petSummary(pet) {
      var card = node('article', 'dj-loyalty-ui__bonus-card');
      card.appendChild(node('h4', '', pet.name));
      card.appendChild(node('p', '', replace(pet.birthDateApproximate ? labels.petSummaryApproxPattern : labels.petSummaryPattern, {
        type:petTypeLabel(pet.type), breed:pet.breed, date:dayDate(pet.birthDate)
      })));
      profileMessages(card, pet.slot);
      var actions = node('div', 'dj-loyalty-ui__actions');
      var edit = node('button', 'dj-loyalty-ui__button dj-loyalty-ui__button--secondary', labels.petEdit);
      edit.type = 'button'; edit.disabled = isMutationBusy() || state.snapshot.programStatus !== 'ACTIVE';
      edit.setAttribute('aria-label', replace(labels.petEditAriaPattern, {name:pet.name}));
      edit.dataset.djFocusKey = 'pet-edit-' + pet.slot;
      edit.addEventListener('click', function () { openPetForm(pet.slot); });
      actions.appendChild(edit); card.appendChild(actions);
      return card;
    }
    function addPetCard(petNumber) {
      var card = node('article', 'dj-loyalty-ui__bonus-card');
      var pill = additionalPetPill();
      if (pill) card.appendChild(pill);
      card.appendChild(node('h4', '', labels.petAddHeading));
      card.appendChild(node('p', '', replace(labels.petAddText, {max:number(state.snapshot.maxPets)})));
      var actions = node('div', 'dj-loyalty-ui__actions');
      var add = node('button', 'dj-loyalty-ui__button dj-loyalty-ui__button--primary', labels.petAddButton);
      add.type = 'button'; add.disabled = isMutationBusy() || state.snapshot.programStatus !== 'ACTIVE';
      add.dataset.djFocusKey = 'pet-edit-' + petNumber;
      add.addEventListener('click', function () { openPetForm(petNumber); });
      actions.appendChild(add); card.appendChild(actions);
      return card;
    }
    function petSection() {
      var snapshot = state.snapshot;
      var wrapper = node('div', 'dj-loyalty-ui__pets');
      wrapper.id = config.petProfileId || '';
      if (!snapshot.pets.length) { wrapper.appendChild(profileForm(1, null)); return wrapper; }
      snapshot.pets.forEach(function (pet) {
        wrapper.appendChild(state.petEditing[pet.slot] ? profileForm(pet.slot, pet) : petSummary(pet));
      });
      if (snapshot.multiPet && snapshot.pets.length < snapshot.maxPets) {
        var nextPetNumber = snapshot.pets.length + 1;
        wrapper.appendChild(state.petEditing[nextPetNumber] ? profileForm(nextPetNumber, null) : addPetCard(nextPetNumber));
      }
      return wrapper;
    }
    function profileForm(petNumber, pet) {
      var profileAction = bonusAction('pet_profile_completed');
      var values = petForm(petNumber);
      var isFirstProfile = petNumber === 1 && !pet;
      var form = node('form', 'dj-loyalty-ui__profile');
      form.noValidate = true;
      if (isFirstProfile && config.profileImageUrl) {
        var profileImage = node('img', 'dj-loyalty-ui__profile-image');
        profileImage.src = config.profileImageUrl;
        profileImage.alt = labels.profileImageAlt || '';
        profileImage.width = 1448;
        profileImage.height = 1086;
        profileImage.loading = 'lazy';
        form.appendChild(profileImage);
      }
      if (isFirstProfile) form.appendChild(node('span', 'dj-loyalty-ui__status-pill' + (profileAction.completed ? ' dj-loyalty-ui__status-pill--complete' : ''), profileAction.completed ? labels.bonusCompleted : labels.profileBonusHelp));
      else if (!pet) { var addPill = additionalPetPill(); if (addPill) form.appendChild(addPill); }
      form.appendChild(node('h3', '', pet ? pet.name : (isFirstProfile ? labels.profileHeading : labels.petAddHeading)));
      form.appendChild(node('p', 'dj-loyalty-ui__help', labels.profileText));
      profileMessages(form, petNumber);
      var grid = node('div', 'dj-loyalty-ui__form-grid');
      var nameField = field(labels.profileName, 'name', 'text', petNumber);
      nameField.input.dataset.djFocusKey = 'pet-name-' + petNumber;
      var breedField = field(labels.profileBreed, 'breed', 'text', petNumber);
      var dateField = field(labels.profileBirthDate, 'birthDate', 'date', petNumber);
      dateField.input.max = localIsoDate(0);
      var typeWrapper = node('div', 'dj-loyalty-ui__field');
      var typeId = instanceId + '-profile-' + (petNumber > 1 ? String(petNumber) + '-' : '') + 'type';
      var typeLabel = node('label', '', labels.profileType); typeLabel.htmlFor = typeId;
      var select = node('select', 'dj-loyalty-ui__select'); select.id = typeId; select.name = 'type'; select.required = true;
      [['', labels.profileTypePlaceholder], ['DOG', labels.profileTypeDog], ['CAT', labels.profileTypeCat], ['HORSE', labels.profileTypeHorse], ['OTHER', labels.profileTypeOther]].forEach(function (optionData) {
        var option = node('option', '', optionData[1]); option.value = optionData[0]; option.selected = values.type === optionData[0]; select.appendChild(option);
      });
      select.addEventListener('change', function () { values.type = select.value; state.profileError = null; state.profileAttemptKeys[petNumber] = null; });
      typeWrapper.append(typeLabel, select);
      if (state.profileError === 'required' && state.profileSlot === petNumber && !String(values.type || '').trim()) {
        var typeError = node('span', 'dj-loyalty-ui__help', labels.fieldRequired); typeError.id = typeId + '-error';
        select.setAttribute('aria-invalid', 'true'); select.setAttribute('aria-describedby', typeError.id); typeWrapper.appendChild(typeError);
      }
      grid.append(nameField.wrapper, typeWrapper, breedField.wrapper, dateField.wrapper); form.appendChild(grid);
      var checkboxLabel = node('label', 'dj-loyalty-ui__checkbox');
      var checkbox = node('input'); checkbox.type = 'checkbox'; checkbox.checked = Boolean(values.birthDateApproximate);
      checkbox.addEventListener('change', function () { values.birthDateApproximate = checkbox.checked; state.profileAttemptKeys[petNumber] = null; });
      checkboxLabel.append(checkbox, node('span', '', labels.profileApproximate)); form.appendChild(checkboxLabel);
      var actions = node('div', 'dj-loyalty-ui__actions');
      var saveLabel = !pet && petNumber > 1 ? labels.petAddSave : labels.profileSave;
      var save = node('button', 'dj-loyalty-ui__button dj-loyalty-ui__button--primary', state.profileSaving && state.profileSlot === petNumber ? labels.profileSaving : saveLabel);
      save.type = 'submit'; save.disabled = isMutationBusy() || state.snapshot.programStatus !== 'ACTIVE'; save.dataset.djFocusKey = petNumber > 1 ? 'profile-save-' + petNumber : 'profile-save'; actions.appendChild(save);
      if (!isFirstProfile) {
        var cancel = node('button', 'dj-loyalty-ui__button dj-loyalty-ui__button--secondary', labels.petCancel);
        cancel.type = 'button'; cancel.disabled = isMutationBusy();
        cancel.addEventListener('click', function () {
          resetPetForm(petNumber); state.petEditing[petNumber] = false; state.profileError = null; state.profileSuccess = null; state.profileAttemptKeys[petNumber] = null;
          state.focusTarget = 'pet-edit-' + petNumber; render();
        });
        actions.appendChild(cancel);
      }
      form.appendChild(actions);
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var complete = ['name', 'type', 'breed', 'birthDate'].every(function (key) { return String(values[key] || '').trim(); });
        if (!complete) { state.profileSlot = petNumber; state.profileError = 'required'; state.profileSuccess = null; state.focusTarget = 'profile-error'; render(); return; }
        saveProfile(petNumber, petNumber === 1 ? profileAction.completed : Boolean(pet));
      });
      return form;
    }
    function awardMessages(target, scope) {
      if (state.awardBusyKey && state.awardBusyKey.indexOf(scope + ':') === 0) {
        target.appendChild(message('info', '', scope === 'birthday' ? labels.birthdayCollecting : labels.reviewChecking, false, 'award-progress'));
      }
      var result = state.awardResult;
      if (!result || result.scope !== scope) return;
      if (result.status === 'error') { target.appendChild(message('error', '', awardErrorText(result), false, 'award-result')); return; }
      var text = scope === 'birthday'
        ? (result.status === 'already' ? labels.birthdayAlready : labels.birthdaySuccess)
        : (result.status === 'already' ? labels.reviewAlready : labels.reviewSuccess);
      target.appendChild(message('success', '', text, false, 'award-result'));
    }
    function awardErrorText(result) {
      var code = String(result && result.code || '');
      var text = '';
      if (code === 'PROGRAM_NOT_ACTIVE') text = labels.programPausedText;
      else if (result.scope === 'birthday') {
        if (code === 'PET_BIRTHDAY_NOT_IN_WINDOW') text = labels.birthdayErrorWindow;
        else if (code === 'PET_PROFILE_TOO_NEW') text = labels.birthdayErrorTooNew;
        return text || labels.birthdayError;
      } else if (code === 'MEDIA_REVIEW_NOT_FOUND') text = labels.reviewErrorNotFound;
      else if (code === 'MEDIA_REVIEW_LIMIT_REACHED') text = labels.reviewErrorLimit;
      else if (code === 'PRODUCT_NOT_IN_LATEST_PURCHASE' || code === 'INVALID_PRODUCT_ID') text = labels.reviewErrorProduct;
      else if (code === 'REVIEW_BONUS_UNAVAILABLE' || code === 'REVIEW_CHECK_UNAVAILABLE') text = labels.reviewErrorUnavailable;
      return text || (result.scope === 'birthday' ? labels.birthdayError : labels.reviewError);
    }
    function birthdaySection() {
      var snapshot = state.snapshot;
      var section = node('section', 'dj-loyalty-ui__section');
      if (!snapshot.petBirthdays.length) {
        // No pet on file yet: still show the gift, otherwise a logged-in customer never sees it
        // (the static guest cards are hidden once the dashboard loads).
        var promoPoints = snapshot.customerAwards.petBirthdayPoints;
        if (!promoPoints) return null;
        section.appendChild(node('h3', '', labels.birthdayHeading));
        var promo = node('article', 'dj-loyalty-ui__bonus-card');
        promo.appendChild(node('span', 'dj-loyalty-ui__status-pill', replace(labels.birthdayPointsPattern, {points:number(promoPoints), amount:pointsValue(promoPoints)})));
        promo.appendChild(node('p', '', replace(labels.birthdayLeadPattern, {points:number(promoPoints), amount:pointsValue(promoPoints)})));
        if (labels.birthdayEmpty) promo.appendChild(node('p', 'dj-loyalty-ui__help', labels.birthdayEmpty));
        section.appendChild(promo);
        return section;
      }
      var giftPoints = snapshot.customerAwards.petBirthdayPoints || snapshot.petBirthdays[0].points;
      section.appendChild(node('h3', '', labels.birthdayHeading));
      section.appendChild(node('p', 'dj-loyalty-ui__help', replace(labels.birthdayLeadPattern, {points:number(giftPoints), amount:pointsValue(giftPoints)})));
      awardMessages(section, 'birthday');
      var grid = node('div', 'dj-loyalty-ui__bonus-grid');
      snapshot.petBirthdays.forEach(function (entry) {
        var values = {
          name:entry.name, year:entry.year || '', points:number(entry.points), amount:pointsValue(entry.points),
          date:dayDate(entry.birthday), opens:dayDate(entry.windowOpensAt), closes:dayDate(entry.windowClosesAt)
        };
        var busyKey = 'birthday:' + entry.slot;
        var card = node('article', 'dj-loyalty-ui__bonus-card');
        var pillText = replace(labels.birthdayPointsPattern, values);
        var text = replace(labels.birthdayUpcomingPattern, values);
        if (entry.claimed) { pillText = replace(labels.birthdayClaimedPill, values); text = replace(labels.birthdayClaimedPattern, values); }
        else if (entry.claimable) { pillText = labels.birthdayReadyPill; text = replace(labels.birthdayClaimablePattern, values); }
        else if (entry.inWindow && !entry.profileOldEnough) text = replace(labels.birthdayTooNewPattern, values);
        card.appendChild(node('span', 'dj-loyalty-ui__status-pill' + (entry.claimed ? ' dj-loyalty-ui__status-pill--complete' : ''), pillText));
        card.appendChild(node('h4', '', entry.name));
        card.appendChild(node('p', '', text));
        if (entry.claimable && !entry.claimed) {
          var actions = node('div', 'dj-loyalty-ui__actions');
          var collect = node('button', 'dj-loyalty-ui__button dj-loyalty-ui__button--primary', state.awardBusyKey === busyKey ? labels.birthdayCollecting : labels.birthdayCollect);
          collect.type = 'button'; collect.disabled = isMutationBusy() || snapshot.programStatus !== 'ACTIVE';
          collect.setAttribute('aria-label', replace(labels.birthdayCollectAriaPattern, values));
          collect.dataset.djFocusKey = 'birthday-collect-' + entry.slot;
          collect.addEventListener('click', function () {
            claimAward('birthday', busyKey, {actionKey:'pet_birthday', slot:entry.slot}, function (fresh) {
              return fresh.petBirthdays.some(function (item) { return item.slot === entry.slot && item.year === entry.year && item.claimed; });
            });
          });
          actions.appendChild(collect); card.appendChild(actions);
        }
        grid.appendChild(card);
      });
      section.appendChild(grid);
      return section;
    }
    function reviewSection() {
      var snapshot = state.snapshot;
      var offer = snapshot.reviewOffer;
      // Nothing at all unless the backend offers the review bonus to this customer.
      if (!offer.enabled || !offer.products.length) return null;
      var values = {points:number(offer.points), amount:pointsValue(offer.points)};
      var section = node('section', 'dj-loyalty-ui__section');
      section.appendChild(node('h3', '', labels.reviewHeading));
      section.appendChild(node('p', 'dj-loyalty-ui__help', replace(labels.reviewLeadPattern, values)));
      awardMessages(section, 'review');
      var grid = node('div', 'dj-loyalty-ui__bonus-grid');
      var rootUrl = String(config.rootUrl || '/').replace(/\/$/, '');
      offer.products.forEach(function (product) {
        var busyKey = 'review:' + product.productId;
        var card = node('article', 'dj-loyalty-ui__bonus-card');
        if (product.imageUrl) {
          var image = node('img', 'dj-loyalty-ui__review-image');
          image.src = product.imageUrl; image.alt = ''; image.width = 72; image.height = 72; image.loading = 'lazy';
          card.appendChild(image);
        }
        card.appendChild(node('span', 'dj-loyalty-ui__status-pill' + (product.awarded ? ' dj-loyalty-ui__status-pill--complete' : ''), product.awarded ? labels.reviewAwarded : replace(labels.reviewPointsPattern, values)));
        card.appendChild(node('h4', '', product.title));
        if (product.orderName) card.appendChild(node('p', '', replace(labels.reviewOrderPattern, {order:product.orderName})));
        if (!product.awarded) {
          var actions = node('div', 'dj-loyalty-ui__actions');
          if (product.handle) {
            var write = node('a', 'dj-loyalty-ui__button dj-loyalty-ui__button--secondary', labels.reviewWrite);
            write.href = rootUrl + '/products/' + encodeURIComponent(product.handle) + '#judgeme_product_reviews';
            write.setAttribute('aria-label', replace(labels.reviewWriteAriaPattern, {title:product.title}));
            actions.appendChild(write);
          }
          var confirm = node('button', 'dj-loyalty-ui__button dj-loyalty-ui__button--primary', state.awardBusyKey === busyKey ? labels.reviewChecking : labels.reviewConfirm);
          confirm.type = 'button'; confirm.disabled = isMutationBusy() || snapshot.programStatus !== 'ACTIVE';
          confirm.setAttribute('aria-label', replace(labels.reviewConfirmAriaPattern, {title:product.title}));
          confirm.dataset.djFocusKey = 'review-confirm-' + product.productId.replace(/[^a-zA-Z0-9_-]/g, '-');
          confirm.addEventListener('click', function () {
            claimAward('review', busyKey, {actionKey:'media_review', productId:product.productId}, function (fresh) {
              return fresh.reviewOffer.products.some(function (item) { return item.productId === product.productId && item.awarded; });
            });
          });
          actions.appendChild(confirm); card.appendChild(actions);
        }
        grid.appendChild(card);
      });
      section.appendChild(grid);
      section.appendChild(node('p', 'dj-loyalty__disclosure', labels.reviewDisclosure));
      return section;
    }
    function voucherCard(reward) {
      var card = node('article', 'dj-loyalty-ui__voucher');
      if (reward.milestone && reward.milestone.title) card.appendChild(node('span', 'dj-loyalty-ui__status-pill dj-loyalty-ui__voucher-milestone', String(reward.milestone.title)));
      card.appendChild(node('strong', '', money(reward.amountCents)));
      card.appendChild(node('span', 'dj-loyalty-ui__voucher-code', reward.code));
      card.appendChild(node('p', 'dj-loyalty-ui__help', replace(labels.expiresPattern, {date:date(reward.expiresAt)})));
      if (labels.voucherUsageWarning) card.appendChild(node('p', 'dj-loyalty-ui__help', labels.voucherUsageWarning));
      var actions = node('div', 'dj-loyalty-ui__actions');
      var copy = node('button', 'dj-loyalty-ui__button dj-loyalty-ui__button--secondary', labels.copy); copy.type = 'button';
      var copyStatus = node('span', 'dj-loyalty-ui__copy-status'); copyStatus.setAttribute('role', 'status');
      copy.addEventListener('click', function () {
        copyText(String(reward.code)).then(function () { copy.textContent = labels.copied; copyStatus.textContent = labels.copied; window.setTimeout(function () { copy.textContent = labels.copy; }, 2200); })
          .catch(function () { copyStatus.textContent = labels.copyFailed; });
      });
      var apply = node('a', 'dj-loyalty-ui__button dj-loyalty-ui__button--primary', labels.apply);
      // The backend's applyPath also adds a product reward to the cart; older rewards fall back to the plain code link.
      apply.href = sameOriginPath(reward.applyPath) || '/discount/' + encodeURIComponent(String(reward.code)) + '?redirect=/cart';
      actions.append(copy, apply); card.append(actions, copyStatus); return card;
    }
    function historyLabel(item) {
      var key = String(item && item.type || '').toUpperCase();
      var actionKey = String(item && (item.actionKey || item.reference) || '').toUpperCase();
      var descriptionKey = String(item && (item.descriptionKey || item.description_key || item.awardType) || '').toLowerCase().replace(/^points\.award\./, '');
      if (descriptionKey === 'pet_birthday') return labels.historyBirthday;
      if (descriptionKey === 'additional_pet') return labels.historyAdditionalPet;
      if (descriptionKey === 'media_review') return labels.historyMediaReview;
      if (descriptionKey === 'game_milestone') return labels.historyGame || 'Poäng från Slottsloppet';
      if (key === 'PENDING') return labels.historyPending;
      if (key === 'REDEEM') return labels.historyRedeem;
      if (key === 'REVERSAL' || key === 'RETURN' || key === 'REFUND') return labels.historyReturn;
      if (key === 'EXPIRE' || key === 'EXPIRY') return labels.historyExpiry;
      if (key === 'REWARD_RETURN' || key === 'RESTORE' || key === 'REDEEM_ROLLBACK' || key === 'VOUCHER_RETURN') return labels.historyRewardReturn;
      if (key === 'MIGRATION') return labels.historyMigration;
      if (key.indexOf('PROFILE') !== -1 || actionKey.indexOf('PROFILE') !== -1) return labels.historyProfileBonus;
      if (key.indexOf('BONUS') !== -1 || key.indexOf('SOCIAL') !== -1 || actionKey.indexOf('FOLLOW') !== -1) return labels.historySocialBonus;
      if (key === 'EARN') return item && item.orderName ? labels.historyEarn : labels.historyBonus;
      return labels.historyAdjustment;
    }
    function historyRow(item) {
      var row = node('li'); var details = node('div');
      details.appendChild(node('p', '', historyLabel(item)));
      var meta = [item.orderName, date(item.createdAt)].filter(Boolean).join(' · ');
      if (meta) details.appendChild(node('small', '', meta));
      var value = Number(item.points) || 0;
      row.append(details, node('strong', value < 0 ? 'dj-loyalty-ui__points--negative' : 'dj-loyalty-ui__points--positive', (value > 0 ? '+' : '') + number(value)));
      return row;
    }
    function render() {
      var focusKey = state.focusTarget;
      var activeElement = document.activeElement;
      if (!focusKey && activeElement && root.contains(activeElement)) focusKey = activeElement.dataset && activeElement.dataset.djFocusKey;
      state.focusTarget = null; root.replaceChildren(); root.setAttribute('aria-busy', state.loading ? 'true' : 'false');
      if (state.loading) {
        var loading = node('div', 'dj-loyalty__loading'); loading.setAttribute('role', 'status'); loading.tabIndex = -1; loading.dataset.djFocusKey = 'loading-status';
        var spinner = node('span', 'dj-loyalty__spinner'); spinner.setAttribute('aria-hidden', 'true'); loading.append(spinner, node('p', '', labels.loading)); root.appendChild(loading); restoreFocus(focusKey); return;
      }
      if (!state.snapshot) { root.appendChild(message('error', labels.loadErrorHeading, labels.loadErrorText, true, 'load-error')); restoreFocus(focusKey || 'load-error'); return; }
      var snapshot = state.snapshot; var ui = node('div', 'dj-loyalty-ui'); ui.tabIndex = -1; ui.dataset.djFocusKey = 'dashboard-content';
      if (snapshot.programStatus !== 'ACTIVE') ui.appendChild(message('warning', labels.programPausedHeading, labels.programPausedText, false));
      if (!snapshot.membership.member) {
        // Not a member yet: the join panel replaces the dashboard; the milestones stay as a preview.
        ui.appendChild(joinPanel());
        var preview = milestoneTrack({
          labels:labels, locale:config.locale, currency:config.currency, heading:labels.milestoneGuestHeading, lead:labels.milestoneLead,
          items:guestMilestones(snapshot.milestones.items), trackPercent:0, nextKey:'', reachedKey:'', messageKey:'none', guest:true, joinUrl:''
        });
        if (preview) ui.appendChild(preview);
        root.appendChild(ui); restoreFocus(focusKey); return;
      }
      if (state.joinSuccess) ui.appendChild(message('success', '', replace(labels.membershipSuccessPattern, {date:date(snapshot.membership.joinedAt, true)}), false, 'join-success'));
      if (snapshot.debtPoints > 0 || snapshot.rawBalancePoints < 0) ui.appendChild(message('info', labels.debtHeading, labels.debtText, false));
      if (snapshot.expiring && Number(snapshot.expiring.points) > 0) ui.appendChild(message('info', replace(labels.expiringPattern, {points:number(snapshot.expiring.points), date:date(snapshot.expiring.expiresAt)}), '', false));
      if (state.redeemError && state.pendingRedemptionAmountKr == null) ui.appendChild(message('error', labels.redeemErrorHeading, state.redeemError === 'uncertain' ? labels.redeemUncertainText : labels.redeemErrorText, false, 'redeem-error'));
      var stats = node('div', 'dj-loyalty-ui__stats');
      stats.appendChild(stat(labels.available, points(snapshot.availablePoints), replace(labels.availableValuePattern, {amount:money(snapshot.redemption.maxAmountKr * 100)})));
      stats.appendChild(stat(labels.pending, points(snapshot.pendingPoints), labels.pendingHelp));
      stats.appendChild(stat(labels.discountValue, money(snapshot.redemption.maxAmountKr * 100), ''));
      var balance = node('div', 'dj-loyalty-ui__balance');
      balance.appendChild(stats);
      // The customer always sees the exact join date (owner decision 2026-09-22).
      if (snapshot.membership.joinedAt) balance.appendChild(node('p', 'dj-loyalty-ui__member-since', replace(labels.membershipSincePattern, {date:date(snapshot.membership.joinedAt, true)})));
      ui.appendChild(balance);
      // Milestones come right after the balance so the next present is the first thing a member sees.
      var milestones = milestoneTrack({
        labels:labels, locale:config.locale, currency:config.currency, heading:labels.milestoneHeading, lead:labels.milestoneLead,
        items:snapshot.milestones.items, trackPercent:snapshot.milestones.trackPercent, nextKey:snapshot.milestones.nextKey,
        reachedKey:snapshot.milestones.reachedKey, messageKey:snapshot.milestones.messageKey,
        guest:false, canClaim:!isMutationBusy() && snapshot.programStatus === 'ACTIVE', busyKey:state.milestoneClaimKey,
        result:state.milestoneResult, cartUrl:config.cartUrl || '/cart', copyText:copyText, onClaim:redeemMilestone
      });
      if (milestones) ui.appendChild(milestones);
      ui.appendChild(redemptionPanel());
      var birthdays = birthdaySection();
      if (birthdays) ui.appendChild(birthdays);
      ui.appendChild(bonusSection());
      var reviews = reviewSection();
      if (reviews) ui.appendChild(reviews);
      else awardMessages(ui, 'review');
      var active = node('section', 'dj-loyalty-ui__section'); active.appendChild(node('h3', '', labels.activeRewards));
      if (!snapshot.activeRewards.length) active.appendChild(node('p', 'dj-loyalty-ui__help', labels.activeEmpty));
      else snapshot.activeRewards.forEach(function (reward) { active.appendChild(voucherCard(reward)); });
      ui.appendChild(active);
      var history = node('section', 'dj-loyalty-ui__section'); history.appendChild(node('h3', '', labels.history));
      if (!snapshot.history.length) history.appendChild(node('p', 'dj-loyalty-ui__help', labels.historyEmpty));
      else { var list = node('ul', 'dj-loyalty-ui__history'); snapshot.history.forEach(function (item) { list.appendChild(historyRow(item)); }); history.appendChild(list); }
      ui.appendChild(history); root.appendChild(ui); restoreFocus(focusKey);
    }
    function load(focusAfterLoad) {
      state.loading = true; state.loadError = false; if (focusAfterLoad) state.focusTarget = 'loading-status'; render();
      request(endpoint).then(function (payload) { state.snapshot = normalize(payload); initializePetForm(state.snapshot); state.loading = false; if (staticBonus) staticBonus.hidden = true; if (focusAfterLoad) state.focusTarget = 'dashboard-content'; render(); })
        .catch(function () { state.snapshot = null; state.loading = false; state.loadError = true; if (staticBonus) staticBonus.hidden = false; state.focusTarget = 'load-error'; render(); });
    }
    function isDefinitiveFailure(error) {
      var status = Number(error && error.status); return Number.isInteger(status) && status >= 400 && status < 500 && status !== 429;
    }
    function activeRewardIds(snapshot) {
      var ids = {}; snapshot.activeRewards.forEach(function (reward) { ids[String(reward.id)] = true; }); return ids;
    }
    function createdReward(fresh, previousIds, matches) {
      return fresh.activeRewards.filter(function (reward) { return !previousIds[String(reward.id)] && matches(reward); })[0] || null;
    }
    // Shared by redeem() and redeemMilestone(): POST once, and when the request fails read the outcome
    // from a fresh snapshot so a retry with the same idempotency key never creates a second code.
    // Resolves {fresh, payload, reward, error}; rejects only when no snapshot could be read at all.
    function submitRedemption(body, previousIds, matches) {
      return request(endpoint + '/redeem', {method:'POST', headers:{Accept:'application/json','Content-Type':'application/json'}, body:JSON.stringify(body)})
        .then(function (payload) {
          return snapshotFromMutation(payload).then(function (fresh) { return {fresh:fresh, payload:payload, reward:createdReward(fresh, previousIds, matches), error:null}; });
        })
        .catch(function (error) {
          return request(endpoint).then(normalize).then(function (fresh) { return {fresh:fresh, payload:null, reward:createdReward(fresh, previousIds, matches), error:error}; });
        });
    }
    function redeem() {
      var amountKr = state.pendingRedemptionAmountKr;
      if (!amountKr || !state.snapshot || state.redeeming) return;
      var idempotencyKey = state.redeemAttemptKey || createIdempotencyKey();
      var previousIds = activeRewardIds(state.snapshot);
      var legacyReward = legacyRewardForAmount(amountKr, false);
      if (!state.snapshot.redemption.flexible && !legacyReward) {
        state.pendingRedemptionAmountKr = null; state.redeemAttemptKey = null; state.redeemError = 'definitive'; state.focusTarget = 'redeem-error'; render(); return;
      }
      var body = {idempotencyKey:idempotencyKey, actionToken:state.snapshot.actionToken};
      if (state.snapshot.redemption.flexible) body.amountKr = amountKr;
      else body.rewardKey = legacyReward.key;
      state.redeemAttemptKey = idempotencyKey; state.redeeming = true; state.redeemError = null; state.redeemSuccess = false; state.focusTarget = 'redemption-confirm'; render();
      submitRedemption(body, previousIds, function (reward) { return Number(reward.amountCents) === amountKr * 100; })
        .then(function (result) {
          state.snapshot = result.fresh; state.redeeming = false;
          if (result.reward || (result.payload && result.payload.redemption)) {
            state.lastRedeemedRewardId = result.reward ? result.reward.id : null;
            state.pendingRedemptionAmountKr = null; state.redemptionAmountKr = ''; state.redeemAttemptKey = null; state.redeemError = null; state.redeemSuccess = true; state.focusTarget = 'redeem-success';
          } else if (isDefinitiveFailure(result.error)) { state.pendingRedemptionAmountKr = null; state.redeemAttemptKey = null; state.redeemError = 'definitive'; state.focusTarget = 'redeem-error'; }
          else { state.redeemError = 'uncertain'; state.focusTarget = 'redeem-error'; }
          render();
        }).catch(function () { state.redeeming = false; state.redeemError = 'uncertain'; state.focusTarget = 'redeem-error'; render(); });
    }
    // Same-origin storefront path from the backend: sets Shopify's discount cookie and, for a
    // product reward, adds the item to the cart. The response body is not needed.
    function applyRewardToCart(path) {
      var safePath = sameOriginPath(path);
      if (!safePath) return;
      try { fetch(safePath, {credentials:'same-origin', redirect:'follow'}).catch(function () {}); } catch (_error) {}
    }
    function redeemMilestone(key) {
      var snapshot = state.snapshot;
      if (!snapshot || isMutationBusy() || snapshot.programStatus !== 'ACTIVE') return;
      var item = milestoneByKey(snapshot.milestones.items, key);
      if (!item || !item.reached) return;
      var attemptKey = state.milestoneAttemptKeys[key] || createIdempotencyKey();
      state.milestoneAttemptKeys[key] = attemptKey;
      var previousIds = activeRewardIds(snapshot);
      state.milestoneClaimKey = key; state.milestoneResult = null; state.awardResult = null; state.actionSuccessKey = null; state.actionErrorKey = null; state.focusTarget = 'milestone-progress'; render();
      submitRedemption({milestoneKey:key, idempotencyKey:attemptKey, actionToken:snapshot.actionToken}, previousIds, function (reward) {
        return Boolean(reward.milestone) && String(reward.milestone.key) === key;
      }).then(function (result) {
        var redemption = result.payload && result.payload.redemption;
        var claimed = result.reward || (redemption && redemption.code ? redemption : null);
        state.snapshot = result.fresh; state.milestoneClaimKey = null;
        if (claimed) {
          var claimedMilestone = claimed.milestone || {};
          state.milestoneAttemptKeys[key] = null;
          state.milestoneResult = {key:key, status:'success', code:'', reward:{
            code:String(claimed.code || ''),
            applyPath:String(claimed.applyPath || ''),
            kind:String(claimedMilestone.kind || item.kind),
            title:String(claimedMilestone.productTitle || claimedMilestone.title || item.productTitle || item.title)
          }};
          applyRewardToCart(state.milestoneResult.reward.applyPath);
        } else if (isDefinitiveFailure(result.error)) {
          if (!retryKeepsAttemptKey(result.error)) state.milestoneAttemptKeys[key] = null;
          state.milestoneResult = {key:key, status:'error', code:String(result.error.code || '')};
        } else {
          state.milestoneResult = {key:key, status:'uncertain', code:''};
        }
        state.focusTarget = 'milestone-result'; render();
      }).catch(function () { state.milestoneClaimKey = null; state.milestoneResult = {key:key, status:'uncertain', code:''}; state.focusTarget = 'milestone-result'; render(); });
    }
    function claimBonus(actionKey) {
      if (!state.snapshot || isMutationBusy() || bonusAction(actionKey).completed) return;
      var attemptKey = state.actionAttemptKeys[actionKey] || createIdempotencyKey();
      state.actionAttemptKeys[actionKey] = attemptKey; state.actionBusyKey = actionKey; state.actionErrorKey = null; state.actionSuccessKey = null; state.awardResult = null; state.focusTarget = 'bonus-progress'; render();
      request(endpoint + '/action', {method:'POST', headers:{Accept:'application/json','Content-Type':'application/json'}, body:JSON.stringify({actionKey:actionKey,idempotencyKey:attemptKey,actionToken:state.snapshot.actionToken})})
        .then(snapshotFromMutation).then(function (fresh) {
          state.snapshot = fresh; state.actionBusyKey = null; state.actionAttemptKeys[actionKey] = null; state.actionSuccessKey = actionKey; state.focusTarget = 'bonus-success'; render();
        }).catch(function () {
          request(endpoint).then(normalize).then(function (fresh) {
            state.snapshot = fresh; state.actionBusyKey = null;
            if (bonusAction(actionKey).completed) { state.actionAttemptKeys[actionKey] = null; state.actionSuccessKey = actionKey; state.focusTarget = 'bonus-success'; }
            else { state.actionErrorKey = actionKey; state.focusTarget = 'bonus-error'; }
            render();
          }).catch(function () { state.actionBusyKey = null; state.actionErrorKey = actionKey; state.focusTarget = 'bonus-error'; render(); });
        });
    }
    function retryKeepsAttemptKey(error) {
      var code = String(error && error.code || '');
      return !isDefinitiveFailure(error) || code === 'ACTION_EXPIRED' || code === 'ACTION_ALREADY_USED' || code === 'TRY_AGAIN';
    }
    // Join (or re-join after a pause): one click, one idempotency key per attempt (kept on uncertain
    // failures). ACTION_EXPIRED reloads the snapshot and retries once with the fresh token; any other
    // failure re-enables the button. The phone travels with the request when the customer typed one;
    // PHONE_REQUIRED / INVALID_PHONE surface on the phone field instead of the generic error.
    function join() {
      var snapshot = state.snapshot;
      if (!snapshot || state.joining || snapshot.membership.member) return;
      var phone = String(state.joinPhone || '').trim();
      if ((snapshot.membership.needsPhone || state.joinPhoneRequested) && !phone) {
        state.joinError = false; state.joinPhoneError = 'required'; state.focusTarget = 'join-phone'; render(); return;
      }
      var attemptKey = state.joinAttemptKey || createIdempotencyKey();
      state.joinAttemptKey = attemptKey; state.joining = true; state.joinError = false; state.joinPhoneError = null; state.focusTarget = 'join-progress'; render();
      submitJoin(attemptKey, snapshot.actionToken, phone, true);
    }
    function submitJoin(attemptKey, actionToken, phone, allowRetry) {
      var body = {idempotencyKey:attemptKey, actionToken:actionToken};
      if (phone) body.phone = phone;
      request(endpoint + '/join', {method:'POST', headers:{Accept:'application/json','Content-Type':'application/json'}, body:JSON.stringify(body)})
        .then(snapshotFromMutation).then(joined)
        .catch(function (error) {
          var code = String(error && error.code || '');
          request(endpoint).then(normalize).then(function (fresh) {
            state.snapshot = fresh;
            if (fresh.membership.member) { joined(fresh); return; }
            if (allowRetry && code === 'ACTION_EXPIRED') { submitJoin(attemptKey, fresh.actionToken, phone, false); return; }
            joinFailed(error);
          }).catch(function () { joinFailed(error); });
        });
    }
    function joined(fresh) {
      state.snapshot = fresh; state.joining = false; state.joinAttemptKey = null; state.joinError = false; state.joinSuccess = true;
      state.joinPhone = ''; state.joinPhoneError = null; state.joinPhoneRequested = false; state.focusTarget = 'join-success'; render();
    }
    function joinFailed(error) {
      var code = String(error && error.code || '');
      state.joining = false;
      if (code === 'PHONE_REQUIRED' || code === 'INVALID_PHONE') {
        // The phone is part of the body, so the corrected attempt is a new request with a new key.
        state.joinError = false; state.joinPhoneRequested = true; state.joinPhoneError = code === 'INVALID_PHONE' ? 'invalid' : 'required';
        state.joinAttemptKey = null; state.focusTarget = 'join-phone'; render(); return;
      }
      state.joinError = true; state.joinPhoneError = null;
      if (!retryKeepsAttemptKey(error)) state.joinAttemptKey = null;
      state.focusTarget = 'join-error'; render();
    }
    function claimAward(scope, busyKey, body, isDone) {
      if (!state.snapshot || isMutationBusy()) return;
      var attemptKey = state.awardAttemptKeys[busyKey] || createIdempotencyKey();
      state.awardAttemptKeys[busyKey] = attemptKey; state.awardBusyKey = busyKey; state.awardResult = null;
      state.actionErrorKey = null; state.actionSuccessKey = null; state.profileSuccess = null; state.focusTarget = 'award-progress'; render();
      request(endpoint + '/action', {method:'POST', headers:{Accept:'application/json','Content-Type':'application/json'}, body:JSON.stringify(Object.assign({}, body, {idempotencyKey:attemptKey, actionToken:state.snapshot.actionToken}))})
        .then(function (payload) {
          return snapshotFromMutation(payload).then(function (fresh) { return {fresh:fresh, payload:payload}; });
        }).then(function (result) {
          var award = result.payload && result.payload.award;
          state.snapshot = result.fresh; state.awardBusyKey = null; state.awardAttemptKeys[busyKey] = null;
          state.awardResult = {scope:scope, status:award && award.awarded === false ? 'already' : 'success', code:''};
          state.focusTarget = 'award-result'; render();
        }).catch(function (error) {
          var code = String(error && error.code || '');
          request(endpoint).then(normalize).then(function (fresh) {
            state.snapshot = fresh; state.awardBusyKey = null;
            if (isDone(fresh)) { state.awardAttemptKeys[busyKey] = null; state.awardResult = {scope:scope, status:'success', code:''}; }
            else {
              if (!retryKeepsAttemptKey(error)) state.awardAttemptKeys[busyKey] = null;
              state.awardResult = {scope:scope, status:'error', code:code};
            }
            state.focusTarget = 'award-result'; render();
          }).catch(function () { state.awardBusyKey = null; state.awardResult = {scope:scope, status:'error', code:code}; state.focusTarget = 'award-result'; render(); });
        });
    }
    function saveProfile(petNumber, alreadyCompleted) {
      if (!state.snapshot || isMutationBusy()) return;
      var values = petForm(petNumber);
      var submitted = {
        name:String(values.name).trim(),
        type:String(values.type).trim(),
        breed:String(values.breed).trim(),
        birthDate:String(values.birthDate).trim(),
        birthDateApproximate:Boolean(values.birthDateApproximate)
      };
      var body = {name:submitted.name, type:submitted.type, breed:submitted.breed, birthDate:submitted.birthDate, birthDateApproximate:submitted.birthDateApproximate};
      if (petNumber > 1) body.slot = petNumber;
      var attemptKey = state.profileAttemptKeys[petNumber] || createIdempotencyKey(); state.profileAttemptKeys[petNumber] = attemptKey;
      body.idempotencyKey = attemptKey; body.actionToken = state.snapshot.actionToken;
      state.profileSlot = petNumber; state.profileSaving = true; state.profileError = null; state.profileSuccess = null; state.awardResult = null; state.focusTarget = 'profile-progress'; render();
      function applied(fresh, kind) {
        var pet = savedPet(fresh, petNumber) || (petNumber === 1 ? fresh.petProfile : null);
        state.petForms[petNumber] = petFormValues(pet || submitted); state.petEditing[petNumber] = false;
        state.profileAttemptKeys[petNumber] = null; state.profileError = null; state.profileSuccess = kind; state.focusTarget = 'profile-success';
      }
      request(endpoint + '/profile', {method:'POST', headers:{Accept:'application/json','Content-Type':'application/json'}, body:JSON.stringify(body)}).then(function (payload) {
        return snapshotFromMutation(payload).then(function (fresh) { return {fresh:fresh, payload:payload}; });
      }).then(function (result) {
        state.snapshot = result.fresh; state.profileSaving = false;
        applied(result.fresh, profileSuccessKind(result.fresh, alreadyCompleted, result.payload, petNumber)); render();
      }).catch(function (error) {
        var code = String(error && error.code || '');
        request(endpoint).then(normalize).then(function (fresh) {
          state.snapshot = fresh; state.profileSaving = false;
          if (profileSaveWasApplied(fresh, submitted, alreadyCompleted, petNumber)) {
            applied(fresh, profileSuccessKind(fresh, alreadyCompleted, null, petNumber));
          } else {
            state.profileError = code === 'INVALID_PET_BIRTH_DATE' ? 'birthDate' : (code === 'PROGRAM_NOT_ACTIVE' ? 'paused' : 'request'); state.focusTarget = 'profile-error';
          }
          render();
        })
          .catch(function () { state.profileSaving = false; state.profileError = 'request'; state.focusTarget = 'profile-error'; render(); });
      });
    }
    load();
  });
})();
